import { useState, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import useReveal from "../lib/useReveal";
import {
  Quote,
  TrendingDown,
  Sparkles,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";

const MUSKAN_IMG =
  "https://customer-assets.emergentagent.com/job_vitality-plan/artifacts/c7c7n3cm_WhatsApp%20Image%202026-06-22%20at%201.00.02%20PM.jpeg";

const SALES_LEAD_IMG =
  "https://customer-assets.emergentagent.com/job_vitality-plan/artifacts/2ohhrpng_WhatsApp%20Image%202026-06-22%20at%201.18.05%20PM.jpeg";

const stories = [
  {
    id: "muskan",
    name: "Muskan",
    profession: "Dentist",
    duration: "Curated 1-on-1 Program",
    primaryMetric: {
      label: "Weight Lost",
      value: "−15 kg",
      sub: "96 kg → 79 kg",
      icon: TrendingDown,
    },
    resultPills: ["Hormonal balance restored", "Energy & confidence reclaimed"],
    quote:
      "Between back-to-back patients all day, eating right or training felt impossible. Aasa built the plan around my life — not the other way around. The weight came off. The hormones settled. I feel like me again.",
    paras: [
      "Muskan is a practicing dentist with a hectic schedule from morning to night — fully engaged in patient care, with very little control over meal times or recovery.",
      "We tailored her nutrition and workout plan around her real day — not a textbook one. She dropped from 96 kg to 79 kg, and her hormonal markers came back into balance.",
    ],
  },
  {
    id: "sales-lead",
    name: "Sales Team Lead",
    profession: "ICICI Lombard · ₹15 Cr / Quarter · Team of 11",
    duration: "Plateau-Break Program · 5 Weeks",
    primaryMetric: {
      label: "Plateau Broken",
      value: "−6 kg",
      sub: "100 kg → 94 kg · 8\" off the waist",
      icon: TrendingDown,
    },
    resultPills: [
      "Stuck for 8 months",
      "Plateau broken in 35 days",
      "−8\" waistline",
    ],
    quote:
      "I was carrying the same 100 kg for almost a year — and a job that doesn't pause. Aasa engineered a plan that actually fit a sales lead's calendar. Six kilos and eight inches gone in just over a month. The needle finally moved.",
    paras: [
      "He leads a team of 11 at ICICI Lombard, owning ₹15 Cr in revenue every quarter — long days, high pressure, very little white space. He had been stuck at 100 kg for the last 6–8 months, no matter what he tried.",
      "We built a targeted plateau-break protocol around his real schedule — calibrated nutrition, strategic strength work and recovery cues. In 1 month and 5 days, he dropped 6 kg and 8 inches off his waistline.",
    ],
  },
];

const STORY_IMAGES = {
  muskan: MUSKAN_IMG,
  "sales-lead": SALES_LEAD_IMG,
};

export const Transformations = () => {
  const ref = useReveal();
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start" });
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setIndex(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    return () => embla.off("select", onSelect);
  }, [embla]);

  return (
    <section
      id="transformations"
      data-testid="transformations-section"
      className="py-16 sm:py-24 lg:py-36 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12" ref={ref}>
        <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-5 sm:gap-6 mb-10 lg:mb-16">
          <div>
            <span className="uppercase text-[10px] sm:text-xs tracking-[0.28em] text-[#C25E3E] font-semibold">
              Transformations
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 sm:mt-6 leading-[1.05] text-[#1E3A2B] max-w-3xl">
              Numbers that move,
              <br />
              <span className="italic">lives that change.</span>
            </h2>
          </div>
          <div
            className="flex items-center gap-3"
            data-testid="transformations-controls"
          >
            <CarouselButton
              direction="prev"
              testid="transformation-prev"
              onClick={() => embla?.scrollPrev()}
            />
            <span
              data-testid="transformation-counter"
              className="text-xs sm:text-sm font-display text-[#1E3A2B]/70 min-w-[3.5rem] text-center"
            >
              {String(index + 1).padStart(2, "0")} /{" "}
              {String(stories.length).padStart(2, "0")}
            </span>
            <CarouselButton
              direction="next"
              testid="transformation-next"
              onClick={() => embla?.scrollNext()}
            />
          </div>
        </div>

        <div className="reveal overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {stories.map((s) => (
              <div
                key={s.id}
                data-testid={`transformation-slide-${s.id}`}
                className="flex-[0_0_100%] min-w-0"
              >
                <StorySlide story={s} image={STORY_IMAGES[s.id]} />
              </div>
            ))}
          </div>
        </div>

        <div
          className="reveal mt-10 flex justify-center items-center gap-3"
          data-testid="transformation-dots"
        >
          {stories.map((s, i) => (
            <button
              key={s.id}
              data-testid={`transformation-dot-${i}`}
              onClick={() => embla?.scrollTo(i)}
              aria-label={`Story ${i + 1}`}
              className={`rounded-full transition-all duration-500 ${
                i === index
                  ? "w-10 h-2.5 bg-[#C25E3E]"
                  : "w-2.5 h-2.5 bg-[#1E3A2B]/25 hover:bg-[#1E3A2B]/50"
              }`}
            />
          ))}
        </div>

        <div className="reveal mt-10 hidden md:flex items-center gap-3 text-[#1E3A2B]/55 justify-center">
          <Sparkles className="w-4 h-4 text-[#C25E3E]" />
          <span className="text-xs uppercase tracking-[0.2em]">
            Real clients · Real numbers · Shared with consent
          </span>
        </div>
      </div>
    </section>
  );
};

const StorySlide = ({ story, image }) => {
  const Metric = story.primaryMetric.icon;
  return (
    <div className="grid lg:grid-cols-12 gap-8 sm:gap-10 items-stretch">
      <div className="lg:col-span-7 relative">
        <div className="relative rounded-3xl overflow-hidden border border-[#D5CEBA] bg-[#FCFBF7]">
          <img
            src={image}
            alt={`Transformation of ${story.name}`}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
          <div className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-[#FCFBF7]/90 backdrop-blur rounded-full px-3 py-1 sm:px-4 sm:py-1.5 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#1E3A2B]">
            {story.duration}
          </div>
          <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 flex flex-wrap items-end justify-between gap-3">
            <div className="bg-[#1E3A2B] text-[#FCFBF7] rounded-2xl px-4 py-3 sm:px-5 sm:py-4">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-70 flex items-center gap-2">
                <Metric className="w-3.5 h-3.5" /> {story.primaryMetric.label}
              </div>
              <div className="mt-1 font-display text-2xl sm:text-3xl">
                {story.primaryMetric.value}
              </div>
              <div className="text-[10px] sm:text-xs opacity-70 mt-0.5">
                {story.primaryMetric.sub}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5 lg:pl-2 flex flex-col">
        <Quote className="w-8 h-8 sm:w-10 sm:h-10 text-[#C25E3E]" />
        <blockquote className="mt-4 font-display italic text-2xl sm:text-3xl lg:text-4xl leading-snug text-[#1E3A2B]">
          &ldquo;{story.quote}&rdquo;
        </blockquote>

        <div className="mt-8 pl-5 sm:pl-6 border-l-2 border-[#C25E3E]">
          <div className="font-display text-2xl text-[#1E3A2B]">
            {story.name}
          </div>
          <div className="mt-1 text-xs sm:text-sm text-[#1E3A2B]/70 uppercase tracking-[0.18em]">
            {story.profession}
          </div>
        </div>

        <div className="mt-8 space-y-3 sm:space-y-4 text-base sm:text-lg text-[#1E3A2B]/80 leading-relaxed">
          {story.paras.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {story.resultPills.map((p) => (
            <span
              key={p}
              className="inline-flex items-center text-[11px] sm:text-xs uppercase tracking-[0.18em] rounded-full bg-[#EAE6DA] text-[#1E3A2B] px-3 sm:px-4 py-2"
            >
              {p}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const CarouselButton = ({ direction, onClick, testid }) => {
  const Icon = direction === "prev" ? ArrowLeft : ArrowRight;
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      aria-label={direction === "prev" ? "Previous story" : "Next story"}
      className="inline-flex w-11 h-11 items-center justify-center rounded-full border border-[#1E3A2B]/30 text-[#1E3A2B] hover:bg-[#1E3A2B] hover:text-[#FCFBF7] transition-colors"
    >
      <Icon className="w-4 h-4" />
    </button>
  );
};

export default Transformations;
